/**
 * Maps a GET /ats_interviews/new response to the wizard's internal payload
 * shape. Covers all three steps (details, communication, scorecard) so
 * payloadRef is fully hydrated for create mode before any step mounts.
 *
 * Pure function — no hooks, no side effects.
 */
import type { ScheduleMode, ScheduleInterviewPayload } from '../config/types';
import { isSelfScheduleMode } from '../config/modeClassifiers';
import { hydrateCommunicationFromApi } from './fromEditApi';
import { SHARED_EMAIL_VALUE_PREFIX } from '../hooks/useScheduleSenderOptions';

interface PlanParticipant {
  id?: number;
  name?: string;
  email?: string;
  avatar?: string;
  avatar_url?: string;
  avatar_thumb_url?: string;
  time_zone?: string;
  timezone?: string;
  // Zoom hosting fields emitted by `_zoom_status.json.jbuilder` on every
  // interviewer / participant. Threaded through `participant_default_options`
  // so the Host field can read each picked interviewer's zoom validity
  // without a separate fetch.
  can_host_zoom_meeting?: boolean;
  zoom_rate_limit_reached?: boolean;
  zoom_plan?: number;
  zoom_plan_type?: string;
}

export function hydratePlanDefaults(
  plan: Record<string, unknown>,
  mode: ScheduleMode
): ScheduleInterviewPayload {
  const slotSelectionEmail = hydrateSlotEmailFromPlan(plan);
  return {
    details: hydrateDetailsFromPlan(plan, mode),
    communication: hydrateCommunicationFromApi(plan),
    scorecard: hydrateScorecardFromPlan(plan),
    ...(slotSelectionEmail ? { slotSelectionEmail } : {}),
  };
}

/**
 * Maps the slot-selection email fields from GET /ats_interviews/new (the
 * plan response) into the Slot Selection Email step's form values.
 *
 * Wire shape — important: the plan response carries the slot-selection
 * email under the NESTED `candidate_self_schedule_email_config` key
 * (emitted via `_comm_configs.json.jbuilder`), NOT the flat
 * `candidate_mail_*` keys that `/ats_schedules/:id` uses on edit. This
 * hydrator initially read the flat keys, so create-from-plan never
 * pre-filled the slot email when the recruiter had set it up at the
 * plan level.
 *
 * Field shape inside `candidate_self_schedule_email_config`:
 *   { id, name, channel, subject, body, attachments,
 *     ats_shared_email: { id, ... } | null,
 *     sender: { id, ... } | null,
 *     cc_participants: [{ id, ... }],
 *     bcc_participants: [{ id, ... }] }
 *
 * Returns undefined when the plan has no slot email configured (the
 * Slot Selection Email step falls back to `getDefaultEmailComposition`).
 */
export function hydrateSlotEmailFromPlan(
  plan: Record<string, unknown>
): Record<string, unknown> | undefined {
  const cfg = plan.candidate_self_schedule_email_config as
    | (Record<string, unknown> & {
        id?: number;
        name?: string;
        subject?: string | null;
        body?: string | null;
        attachments?: unknown[];
        sender?: { id?: number | string } | null;
        ats_shared_email?: { id?: number | string } | null;
        cc_participants?: Array<{ id?: number }>;
        bcc_participants?: Array<{ id?: number }>;
      })
    | undefined;

  // Treat empty objects as "no slot email configured" — the BE emits
  // `{}` when the comm config row exists but isn't filled in yet.
  if (
    !cfg ||
    typeof cfg !== 'object' ||
    Array.isArray(cfg) ||
    Object.keys(cfg).length === 0
  ) {
    return undefined;
  }

  // Resolve the From dropdown value. Shared mailbox wins; otherwise use
  // the saved sender id; otherwise leave blank (the slot-email step
  // resolves a sensible default at mount time).
  const sharedId =
    cfg.ats_shared_email?.id !== undefined && cfg.ats_shared_email?.id !== null
      ? Number(cfg.ats_shared_email.id)
      : undefined;
  const senderId =
    cfg.sender?.id !== undefined && cfg.sender?.id !== null
      ? String(cfg.sender.id)
      : '';
  const fromValue =
    sharedId !== undefined && Number.isFinite(sharedId) && sharedId > 0
      ? `${SHARED_EMAIL_VALUE_PREFIX}${sharedId}`
      : senderId;

  const toIds = (val: Array<{ id?: number }> | undefined): string[] =>
    Array.isArray(val)
      ? val
          .map((u) => (u?.id !== undefined ? String(u.id) : ''))
          .filter(Boolean)
      : [];

  return {
    subject: cfg.subject ?? '',
    content: cfg.body ?? '',
    from: fromValue,
    cc: toIds(cfg.cc_participants),
    bcc: toIds(cfg.bcc_participants),
    // The slot-email step's `template` dropdown is for switching to a
    // saved template — leave null on hydration since the plan's saved
    // copy is a one-time template snapshot. `_hydrated_template_id`
    // is informational only.
    template: null,
    _hydrated_template_id: typeof cfg.id === 'number' ? cfg.id : null,
  };
}

function hydrateDetailsFromPlan(
  plan: Record<string, unknown>,
  mode: ScheduleMode
): Record<string, unknown> {
  const interviewName =
    (plan.substituted_name as string) ?? (plan.name as string) ?? '';

  const interview_type =
    (plan.kind as string) ?? (plan.interview_kind as string) ?? 'one_on_one';

  // Plan emits `interviewers`; edit API emits `participants` — accept both.
  const participants: PlanParticipant[] = Array.isArray(plan.participants)
    ? (plan.participants as PlanParticipant[])
    : Array.isArray(plan.interviewers)
      ? (plan.interviewers as PlanParticipant[])
      : [];

  // For one_on_one with multiple participants, store an empty array so the
  // form starts with no pre-selection. The recommended-interviewer pill will
  // suggest one and the full pool is available via participant_default_options.
  // When there's only one option there's nothing to recommend — pre-fill it.
  // Panel/self-schedule always send all participants (everyone attends).
  const allParticipantIds = participants
    .map((p) => String(p.id))
    .filter(Boolean);
  const isSelfSchedule = isSelfScheduleMode(mode);
  const isOneOnOne = interview_type === 'one_on_one';
  // Manual one-on-one with a pool: leave empty so the recommended-interviewer
  // pill can appear after a slot is picked. Self-schedule always pre-fills all
  // participants (the pool mechanic doesn't apply there).
  const participant_ids =
    !isSelfSchedule && isOneOnOne && allParticipantIds.length > 1
      ? []
      : allParticipantIds;

  const participant_default_options = participants
    .filter((p) => p.id !== undefined)
    .map((p) => ({
      value: String(p.id),
      label: p.name ?? p.email ?? `User ${p.id}`,
      description: p.email,
      image: p.avatar ?? p.avatar_url ?? p.avatar_thumb_url,
      zoomStatus: {
        can_host_zoom_meeting: p.can_host_zoom_meeting,
        zoom_rate_limit_reached: p.zoom_rate_limit_reached,
        zoom_plan: p.zoom_plan,
        zoom_plan_type: p.zoom_plan_type,
      },
      data: { timezone: p.time_zone ?? p.timezone ?? '' },
    }));

  const duration =
    typeof plan.duration === 'number' && plan.duration > 0 ? plan.duration : 30;

  // Store '' when plan has no location or 'unspecified'.
  // DetailsForm's useEffect fills the async account-settings fallback.
  const planLocation = plan.location as string | undefined;
  const location =
    planLocation && planLocation !== 'unspecified' ? planLocation : '';

  const hostObj =
    typeof plan.host === 'object' && plan.host !== null
      ? (plan.host as Record<string, unknown>)
      : null;
  const host_id = hostObj?.id ? String(hostObj.id) : '';

  const officeObj =
    typeof plan.ats_office === 'object' && plan.ats_office !== null
      ? (plan.ats_office as Record<string, unknown>)
      : null;
  const rawOfficeId =
    plan.ats_office_id !== undefined && plan.ats_office_id !== null
      ? plan.ats_office_id
      : officeObj?.id;
  const ats_office_id =
    rawOfficeId !== undefined && rawOfficeId !== null
      ? String(rawOfficeId)
      : '';

  const meetingRooms = Array.isArray(plan.meeting_rooms)
    ? (plan.meeting_rooms as Array<{ email?: string }>)
        .map((r) => r.email ?? '')
        .filter(Boolean)
    : [];

  const planCal = plan.calendar_info as
    | { provider_calendar_id?: string; is_primary?: boolean }
    | null
    | undefined;
  const calendar_info = planCal?.provider_calendar_id
    ? {
        provider_calendar_id: planCal.provider_calendar_id,
        is_primary: Boolean(planCal.is_primary),
      }
    : null;

  const hrNested = plan.hackerrank_interview as
    | { ext_interview_template_id?: string | number }
    | undefined;
  const hrTemplate =
    hrNested?.ext_interview_template_id ??
    (plan.ext_interview_template_id as string | number | undefined);
  const ext_interview_template_id =
    hrTemplate !== undefined && hrTemplate !== null ? String(hrTemplate) : null;

  return {
    interviewName,
    interview_type,
    participant_ids,
    participant_default_options,
    duration,
    location,
    host_id,
    ats_office_id,
    book_meeting_room: Array.isArray(meetingRooms) && meetingRooms.length > 0,
    // isMulti: false on the meeting-room Select — seed as a single string,
    // not an array. Multiple rooms aren't supported in the new wizard's UI;
    // take the first room from the plan. Array-guard the index access in case
    // an upstream change ever returns a non-array shape.
    meeting_room_ids: Array.isArray(meetingRooms)
      ? (meetingRooms[0] ?? '')
      : '',
    calendar_info,
    calendar_info_picker: calendar_info?.provider_calendar_id ?? '',
    dateTime: '',
    startDate: null,
    startTime: '',
    endTime: '',
    isDateTimeEditing: 'view',
    isCalendarEditing: 'view',
    findTime: '',
    calendar_visibility:
      plan.calendar_visibility === 'private' ||
      plan.calendar_visibility === true,
    candidatePhone: '',
    candidate_phone: undefined,
    isCandidatePhoneEditing: 'edit',
    // ON when the plan has it enabled OR when account/job marks it mandatory —
    // mirrors the activity-setup logic: note_taker_mandatory || note_taker_enabled.
    enable_note_taker:
      Boolean(plan.note_taker_mandatory) || Boolean(plan.note_taker_enabled),
    ext_interview_template_id,
  };
}

function hydrateScorecardFromPlan(
  plan: Record<string, unknown>
): Record<string, unknown> {
  return {
    enabled: Boolean(plan.scorecard_required),
    template_id: '',
    is_feedback_mandatory: false,
    questions: [],
  };
}
